import { ReactNode, useEffect, useState } from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useRoles } from "@/hooks/useRole";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

// Routes that remain reachable without a signed-in session.
// Includes all footer-nav destinations so logged-out visitors can browse the
// public surface area. RLS on the backend continues to gate sensitive data.
const PUBLIC_PATHS = [
  "/auth",
  "/unsubscribe",
  "/share",
  "/communities",
  // Platform
  "/ideas",
  "/opportunities",
  "/funders",
  "/legislation",
  "/insights",
  "/events",
  "/intelligence",
  "/for-funders",
  "/for-agencies",
  "/developers",
  // Library
  "/archive",
  "/data-tools",
  // Communities
  "/communities",
  // Network

  "/partners",
  "/affiliates",
  // Company
  "/about",
  "/about-us",
  "/services",
  "/pricing",
  "/status",
  "/help",
  "/faq",
  "/contact",
  // Legal
  "/privacy",
  "/terms",
  "/cookies",
  "/acceptable-use",
  "/retention",
  "/dpa",
  "/security",
  "/incident-response",
  "/trust",
  "/accessibility",
  "/roadmap",
  "/changelog",
  "/methodology",
];

// Where signed-in users may go even while MFA is pending (account settings + the MFA page itself).
const MFA_BYPASS_PATHS = ["/auth/mfa", "/account"];

// Routes signed-in users can always reach during onboarding,
// even while they're on the waitlist.
const SIGNED_IN_ONBOARDING_PATHS = ["/welcome"];

function isPublicPath(pathname: string) {
  if (pathname === "/") return true;
  return PUBLIC_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

const Splash = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <Loader2 className="h-6 w-6 animate-spin text-primary" />
  </div>
);

const WaitlistHold = ({ expired = false }: { expired?: boolean } = {}) => {
  const { user, signOut } = useAuth();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-8 text-center shadow-elegant">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <h1 className="font-display text-2xl font-semibold">
          {expired ? "Your demo access has ended" : "You're on the waitlist"}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {expired
            ? `Your guest preview${user?.email ? ` (${user.email})` : ""} has expired. Reach out to The Emerging Market team to extend access.`
            : `The Emerging Market is in private beta. Your account${user?.email ? ` (${user.email})` : ""} is recognized, but full access is invite-only right now. We'll email you when your seat opens.`}
        </p>
        <div className="mt-6 flex flex-col gap-2">
          <Button variant="outline" onClick={() => signOut()}>
            Sign out
          </Button>
          <Button asChild variant="ghost" size="sm">
            <Link to="/status">View system status</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

const AccessGate = ({ children }: { children: ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const {
    isSuperAdmin,
    isGuest,
    isMember,
    isGuestExpired,
    isPartnerAdmin,
    loading: rolesLoading,
  } = useRoles();
  const location = useLocation();
  const [mfaState, setMfaState] = useState<
    "checking" | "ok" | "upgrade" | "missing-admin-factor"
  >("checking");

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setMfaState("ok");
      return;
    }
    const onMfaBypass = MFA_BYPASS_PATHS.some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
    );
    if (onMfaBypass) {
      setMfaState("ok");
      return;
    }
    (async () => {
      try {
        const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (cancelled) return;
        if (aal && aal.nextLevel === "aal2" && aal.currentLevel === "aal1") {
          setMfaState("upgrade");
          return;
        }
        const { data: factors } = await supabase.auth.mfa.listFactors();
        if (cancelled) return;
        const verifiedTotp = factors?.totp?.find((f) => f.status === "verified");
        const adminLike = isSuperAdmin || isPartnerAdmin;
        if (adminLike && !verifiedTotp) {
          setMfaState("missing-admin-factor");
          return;
        }
        setMfaState("ok");
      } catch {
        if (!cancelled) setMfaState("ok");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, isSuperAdmin, isPartnerAdmin, location.pathname]);

  if (authLoading) return <Splash />;

  // Always allow public utility routes.
  if (isPublicPath(location.pathname)) return <>{children}</>;

  // No session → send visitors to the public community homepage.
  if (!user) return <Navigate to="/" replace />;

  // Wait for role lookup before deciding for signed-in users.
  if (rolesLoading) return <Splash />;

  if (mfaState === "checking") return <Splash />;

  const onMfaBypass = MFA_BYPASS_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
  );

  if (mfaState === "upgrade" && !onMfaBypass) {
    return <Navigate to="/auth/mfa" replace />;
  }

  if (mfaState === "missing-admin-factor" && !onMfaBypass) {
    return <Navigate to="/account?tab=security&enroll=1" replace />;
  }

  // Strict admin dashboard access: only the owner email, regardless of role.
  if (location.pathname.startsWith("/admin")) {
    if (isSuperAdmin && user?.email?.toLowerCase() === "emergingmrkt@gmail.com") {
      return <>{children}</>;
    }
    return <WaitlistHold />;
  }

  if (isSuperAdmin) return <>{children}</>;

  // Affiliate / partner admins can reach their dashboard regardless of waitlist status.
  if (isPartnerAdmin && location.pathname.startsWith("/affiliate/dashboard")) {
    return <>{children}</>;
  }

  if (isGuest) {
    // Guests get the full app except /admin
    if (location.pathname.startsWith("/admin")) {
      return <WaitlistHold />;
    }
    return <>{children}</>;
  }

  if (isMember) {
    // Members get the full app except /admin
    if (location.pathname.startsWith("/admin")) {
      return <WaitlistHold />;
    }
    return <>{children}</>;
  }

  if (isGuestExpired) return <WaitlistHold expired />;

  // Signed-in but waitlisted users can still reach /welcome to finish setup.
  if (
    SIGNED_IN_ONBOARDING_PATHS.some(
      (p) =>
        location.pathname === p || location.pathname.startsWith(`${p}/`),
    )
  ) {
    return <>{children}</>;
  }

  return <WaitlistHold />;
};

export default AccessGate;